<template>
  <div v-if="isActive" class="modal-popup" @click="handleOverlayClick">
    <div class="popup instagram-native-popup" @click.stop>
      <!-- Header with Top Network Logo & Native Flame Badge (Centered) -->
      <div class="onboarding-header">
        <div class="onboarding-network-icon-wrapper">
          <img
            :src="instagramIcon"
            alt="Instagram"
            class="onboarding-network-icon"
          />
        </div>
        <div class="onboarding-header-content">
          <h3 class="onboarding-header-title">
            Instagram Native
            <svg class="native-icon" width="20" height="20" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" fill="#111827"><path d="M37.34,7.36a.12.12,0,0,1,.18.13c-.47,1.86-2.78,12.63,5.57,19.62,8.16,6.84,8.41,17.13,2.33,24-7.27,8.23-19.84,6.78-25.25,1.37C16.36,48.69,9.44,36.33,21.29,26a.1.1,0,0,1,.16,0c.29,1.23,2.3,9,7.66,10,.25,0,.37-.11.25-.34C27.78,32.6,20.66,17,37.34,7.36Z" stroke-linecap="round"/></svg>
          </h3>
          <p class="onboarding-header-subtitle">
            {{ $t('onboarding.connect_pages.instagram_native_comparison') }}
          </p>
        </div>
      </div>

      <!-- Feature badges (Centered) -->
      <div class="onboarding-features-badges">
        <span
          class="onboarding-feature-badge"
          v-for="feature in instagramFeatures"
          :key="feature"
          :data-tooltip="$t(`onboarding.features.${feature}.description`)"
        >
          {{ $t(`onboarding.features.${feature}.title`) }}
        </span>
      </div>

      <!-- Native connection form -->
      <div class="onboarding-native-form">
        <!-- Error message -->
        <div v-if="error" class="onboarding-message onboarding-message-error">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>{{ error }}</span>
        </div>

        <div class="onboarding-form-group">
          <label class="onboarding-form-label">
            {{ $t('onboarding.connect_natives.form_label_instagram_username') }}
          </label>
          <input
            type="text"
            v-model="credentials.username"
            class="onboarding-form-input"
            placeholder="Ex: nom_compte"
            ref="usernameInput"
            autocomplete="username"
            autocapitalize="none"
            autocorrect="off"
            spellcheck="false"
          />
        </div>

        <div class="onboarding-form-group">
          <label class="onboarding-form-label">
            {{ $t('onboarding.connect_natives.form_label_password') }}
          </label>
          <input
            type="password"
            v-model="credentials.password"
            class="onboarding-form-input"
            placeholder=""
            ref="passwordInput"
            @keyup.enter="handleConnect"
          />
        </div>

        <!-- Buttons -->
        <div class="buttons modal-buttons">
          <div class="button btn-outline-primary" @click="handleCancel">
            {{ $t('onboarding.bluesky.button_cancel') }}
          </div>
          <button
            class="button btn-primary onboarding-btn-connect"
            :disabled="!canConnect || isConnecting"
            @click="handleConnect"
          >
            <span v-if="isConnecting" class="onboarding-btn-loading">
              <svg class="onboarding-spinner" width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path opacity="0.2" d="M10 1C5.02944 1 1 5.02944 1 10C1 14.9706 5.02944 19 10 19C14.9706 19 19 14.9706 19 10C19 5.02944 14.9706 1 10 1Z" stroke="currentColor" stroke-width="2"/>
                <path d="M10 1C14.9706 1 19 5.02944 19 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              {{ $t('onboarding.connect_natives.button_connecting') }}
            </span>
            <span v-else>
              {{ $t('onboarding.connect_natives.button_connect') }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getNetworkIcon } from '../utils/networkMaps';

export default {
  name: 'InstagramNativeModal',

  props: {
    active: {
      type: Boolean,
      default: false
    },
    initialUsername: {
      type: String,
      default: ''
    },
    error: {
      type: String,
      default: null
    },
    isConnecting: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isActive: false,
      credentials: {
        username: '',
        password: ''
      },
      instagramFeatures: [
        'instagram.native_post',
        'instagram.stories',
        'instagram.mention',
        'instagram.music',
        'instagram.hashtag',
        'instagram.sticker'
      ]
    }
  },

  computed: {
    canConnect() {
      return this.credentials.username.trim() !== '' && this.credentials.password.trim() !== ''
    },
    instagramIcon() {
      return getNetworkIcon('instagram')
    }
  },

  watch: {
    active(newVal) {
      this.isActive = newVal
      if (newVal) {
        const raw = this.initialUsername || ''
        this.credentials = { 
          username: raw.trim().replace(/[\s@]/g, ''), 
          password: '' 
        }
        this.$nextTick(() => {
          if (this.credentials.username && this.$refs.passwordInput) {
            this.$refs.passwordInput.focus()
          } else if (this.$refs.usernameInput) {
            this.$refs.usernameInput.focus()
          }
        })
      }
    }
  },

  methods: {
    handleOverlayClick(e) {
      if (e.target.classList.contains('modal-popup')) {
        this.handleCancel()
      }
    },

    handleCancel() {
      this.$emit('cancel')
    },

    handleConnect() {
      if (!this.canConnect || this.isConnecting) return
      const cleanUsername = this.credentials.username.trim().replace(/[\s@]/g, '')
      this.$emit('connect', {
        username: cleanUsername,
        password: this.credentials.password.trim()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../variables.scss";

.modal-popup {
  z-index: 10000 !important;
}

.instagram-native-popup {
  width: 500px !important;
  max-width: 90vw;
  padding: 32px !important;
  border-radius: 16px !important;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center !important;
  gap: 18px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  padding: 45px 55px !important;

  .onboarding-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 12px;

    .onboarding-network-icon-wrapper {
      position: relative;
      flex-shrink: 0;

      .onboarding-network-icon {
        width: 52px;
        height: 52px;
        border-radius: 50%;
      }
    }

    .onboarding-header-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;

      .onboarding-header-title {
        font-size: 20px;
        font-weight: 700;
        color: #111827;
        margin: 0 0 6px 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        line-height: 1.2;

        .native-icon {
          flex-shrink: 0;
        }
      }

      .onboarding-header-subtitle {
        font-size: 13px;
        color: #4b5563;
        margin: 0;
        line-height: 1.4;
        text-align: center;
      }
    }
  }

  .onboarding-features-badges {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
    margin-bottom: 20px;

    .onboarding-feature-badge {
      padding: 4px 10px;
      background: $bg-ui-selected;
      color: $primary-color;
      font-size: 12px;
      font-weight: 500;
      border-radius: 14px;
    }
  }

  .onboarding-native-form {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;

    .onboarding-message-error {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px;
      background: #fef2f2;
      border: 1px solid #fca5a5;
      border-radius: 8px;
      color: #991b1b;
      font-size: 13px;

      svg {
        flex-shrink: 0;
      }
    }

    .onboarding-form-group {
      display: flex;
      flex-direction: column;
      gap: 6px;

      .onboarding-form-label {
        font-size: 14px;
        font-weight: 600;
        color: #374151;
      }

      .onboarding-form-input {
        width: 100%;
        padding: 10px 14px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        font-size: 14px;
        color: #111827;
        transition: border-color 0.15s ease;

        &:focus {
          outline: none;
          border-color: $primary-color;
        }
      }
    }

    .modal-buttons {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-top: 20px;

      .button {
        flex: 1;
        padding: 10px 16px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        text-align: center;
        cursor: pointer;
        transition: all 0.15s ease;
      }

      .btn-outline-primary {
        border: 1px solid #d1d5db;
        color: #374151;
        background: #ffffff;

        &:hover {
          background: #f3f4f6;
        }
      }

      .btn-primary {
        background: $primary-color;
        color: #ffffff;
        border: none;

        &:hover:not(:disabled) {
          opacity: 0.9;
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .onboarding-btn-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;

          .onboarding-spinner {
            animation: spin 1s linear infinite;
          }
        }
      }
    }
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
